interface ApiExceptionShape {
  isApiException: true
  status: number
  response: string
  message: string
}

interface ProblemDetailsError {
  property?: string | null
  error?: string | null
}

interface ProblemDetails {
  errors?: ProblemDetailsError[]
  title?: string
  detail?: string
}

function isApiException(err: unknown): err is ApiExceptionShape {
  return (
    err !== null &&
    typeof err === 'object' &&
    (err as { isApiException?: unknown }).isApiException === true
  )
}

function isProblemDetails(err: unknown): err is ProblemDetails {
  return (
    err !== null &&
    typeof err === 'object' &&
    Array.isArray((err as { errors?: unknown }).errors)
  )
}

function parseProblemDetails(body: string): ProblemDetails | null {
  if (body === '') return null
  try {
    const parsed: unknown = JSON.parse(body)
    if (parsed === null || typeof parsed !== 'object') return null
    return parsed as ProblemDetails
  } catch {
    return null
  }
}

// The SDK's generated `throwException` throws the parsed body directly when
// the response status maps to a schema (e.g. 400/412 ProblemDetails); only
// undefined-status responses get wrapped in ApiException. So we try both.
function asProblemDetails(err: unknown): ProblemDetails | null {
  if (isProblemDetails(err)) return err
  if (isApiException(err)) return parseProblemDetails(err.response)
  return null
}

export function extractApiErrorMessages(err: unknown): string[] {
  const problem = asProblemDetails(err)
  if (problem === null) return []
  const out: string[] = []
  for (const e of problem.errors ?? []) {
    const message = e.error?.trim()
    if (typeof message === 'string' && message !== '') out.push(message)
  }
  if (out.length > 0) return out
  if (problem.title && problem.title.trim() !== '') return [problem.title.trim()]
  return []
}

export function extractApiErrorTitle(err: unknown): string | null {
  const problem = asProblemDetails(err)
  if (problem?.title && problem.title.trim() !== '') return problem.title.trim()
  if (isApiException(err) && err.message && err.message.trim() !== '') return err.message.trim()
  return null
}
